/**
 * useAiThumbnails
 *
 * Drives a seek-bar preview ("trickplay") generation job and exposes the same
 * small state machine shape as useAiSubtitles / useAiDubbing, so the UI renders a
 * familiar progress panel. Owns no form knowledge — it runs the service, tracks
 * stage/progress/note, and hands back the finished { vttUrl, spriteUrl, … }. The
 * Media Assets section decides what to do with it (store on the content so the
 * web-app player can show scene previews while scrubbing).
 *
 * Status lifecycle:
 *   idle → running → complete
 *                 ↳ failed  (retry via generate())
 *                 ↳ idle    (reset())
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { generateThumbnails, THUMBNAIL_STAGES } from "../services/ai";

export const THUMBNAIL_STATUS = {
  IDLE: "idle",
  RUNNING: "running",
  COMPLETE: "complete",
  FAILED: "failed",
};

export default function useAiThumbnails() {
  const [status, setStatus] = useState(THUMBNAIL_STATUS.IDLE);
  const [activeStage, setActiveStage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [note, setNote] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus(THUMBNAIL_STATUS.IDLE);
    setActiveStage(null);
    setProgress(0);
    setNote(null);
    setResult(null);
    setError(null);
  }, []);

  /**
   * Start (or restart) thumbnail generation for a video.
   * @param {object} video
   * @param {object} [opts] { interval }
   */
  const generate = useCallback((video, opts = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus(THUMBNAIL_STATUS.RUNNING);
    setError(null);
    setResult(null);
    setProgress(0);
    setNote(null);
    setActiveStage(THUMBNAIL_STAGES[0]?.id ?? null);

    generateThumbnails({
      video,
      interval: opts.interval,
      signal: controller.signal,
      onStage: (stageId, pct, stageNote) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        if (stageId !== "complete") setActiveStage(stageId);
        if (typeof pct === "number") setProgress(pct);
        if (stageNote !== undefined) setNote(stageNote || null);
      },
    })
      .then((res) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        setResult(res);
        setProgress(100);
        setActiveStage("complete");
        setStatus(THUMBNAIL_STATUS.COMPLETE);
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        if (!mountedRef.current) return;
        setError(err?.message || "Thumbnail generation failed. You can retry.");
        setStatus(THUMBNAIL_STATUS.FAILED);
      });

    return controller;
  }, []);

  return {
    status,
    stages: THUMBNAIL_STAGES,
    activeStage,
    progress,
    note,
    result,
    error,
    generate,
    reset,
    isIdle: status === THUMBNAIL_STATUS.IDLE,
    isRunning: status === THUMBNAIL_STATUS.RUNNING,
    isComplete: status === THUMBNAIL_STATUS.COMPLETE,
    isFailed: status === THUMBNAIL_STATUS.FAILED,
  };
}
